/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import {
  ATTENDANCE_WORK_CIRCUMSTANCES,
  KIND_OF_REPORTS,
} from '@src/containers/App/TimeTracker/constants';
import { translateString } from '@src/util/intl/translateString';

import { DateUtil } from '@src/packages/utils/dates';
import { camelize } from '@src/packages/utils/functions';
import moment from 'moment-timezone';
import { useTimeTracker } from '../contexts/TimeTrackerProvider';
import {
  GLOSSARY_ITEMS,
  LABEL_REPORTS,
  PDF_REPORTS_COLUMNS,
} from '../Main/components/reports/shared/constants';
import { getColumnColor } from '../Main/components/reports/shared/getColorColumn';
import { Button, Tooltip } from 'antd';
import { ReportContext } from '../Main/contexts/ReportContext';
import { useRulesByCountry } from '@src/packages/utils/hooks/useRulesByCountry';
import { ACCESS_TYPES } from '@src/packages/utils';
import { useSelector } from 'react-redux';
import useGetTimeZone from '@src/packages/utils/hooks/useGetTimeZone';
import { mergeReportData } from '../Main/utils/mergeReportData';
import { formatObservation } from '../Main/utils/formatObservation';
import TimeTrackerUtils from '../utils';
import { useModificationReport } from './useModificationReport';

const DATE_FORMAT = DateUtil.DEFAULT_DT_DATE_FORMAT;
/** Cooldown after an attendance update before the row can be edited again (ms). */
const ATTENDANCE_EDIT_COOLDOWN_MS = 48 * 60 * 60 * 1000;

/**
 * Factory to create the report data
 * @param {Object} param0.data - The data to be factorized
 * @param {String} param0.reportType - The type of report to be created
 * @param {String} param0.queriedPeriod - The period to be queried
 * @param {Boolean} param0.reset - If the data should be reset
 * @returns {Object}
 */

/**
 * Returns the ordinary working hours.
 * @returns {Object} An object with start and end times.
 * @example
 * Return the Journey Start Date.
 * Return the Journey Finish Date.
 * {
 *  scheduled_start_at,
 *  scheduled_end_at
 * }
 * Return the Marking working hours.
 * {
 *   actual_start_at,
 *   actual_end_at
 * }
 * Return collation
 * {
 *  schedule_break_start_at,
 *  schedule_break_end_at
 * }
 * Return Marking collation
 * {
 *   actual_break_start_at,
 *   actual_break_end_at
 * }
 * Return D.E.J
 * {
 *  exceptional_shift
 * }
 * Return Others markings
 * {
 *  other_shift_information
 * }
 * Return the date
 * {
 *  scheduled_start_at
 * }
 */

const useTimeTrackerReportFactory = ({ data, reportType, queriedPeriod, reset }) => {
  const { isFromOperonda, country, setReportData: setReportDataTimeTracker } = useTimeTracker();
  const userTimeZone = useGetTimeZone();

  // Store the timezone used for conversion to pass to getRangeTime
  const getTargetTimeZone = tz => {
    if (!isFromOperonda) {
      return 'America/Santiago';
    }
    return tz || null;
  };
  const orgUser = useSelector(({ user }) => user.orgUser.data);
  const isAdminOrOwner =
    orgUser?.access === ACCESS_TYPES.ADMIN || orgUser?.access === ACCESS_TYPES.OWNER;
  const { countryRulesForm } = useRulesByCountry({ countryName: country });
  const formatIdentifier = countryRulesForm.identifier.formatIdentifier;

  const { setSelectedReportRecord, setIsDrawerOpen, isAllUsersSelected } = useContext(ReportContext);
  const { usersSelectedShiftWorkIds } = useModificationReport();

  const [reportData, setReportData] = useState({});
  const [error, setError] = useState(null);

  const intl = useIntl();

  // Normalize incoming payload to camelCase to support backend change
  const camelData = useMemo(() => (data ? camelize(data) : data), [data]);

  const translateValue = value => {
    const prefix = 'work.direction.modifications.value';
    return intl.formatMessage({ id: `${prefix}.${value}` });
  };

  const convertToDate = (seconds = 0, nanoseconds = 0) => {
    if (seconds !== 0 || nanoseconds !== 0) return DateUtil.toDate(seconds, nanoseconds);
    return null;
  };

  /**
   * Disables edit while `updatedAttendanceAt` is within the cooldown window (device clock).
   * After {@link ATTENDANCE_EDIT_COOLDOWN_MS}, editing is allowed again.
   */
  const isAttendanceEditInCooldown = updatedAttendanceAt => {
    if (!updatedAttendanceAt) return false;
    const seconds = updatedAttendanceAt.seconds ?? updatedAttendanceAt._seconds;
    const nanoseconds = updatedAttendanceAt.nanoseconds ?? updatedAttendanceAt._nanoseconds ?? 0;
    if (seconds == null) return false;
    const updatedDate = convertToDate(seconds, nanoseconds);
    if (!updatedDate) return false;
    return Date.now() - updatedDate.getTime() < ATTENDANCE_EDIT_COOLDOWN_MS;
  };

  const getDateTimeString = date => {
    return DateUtil.toDateString(date, `${DATE_FORMAT} HH:mm`);
  };

  const generalYesNo = (pVal = false) => {
    return pVal
      ? intl.formatMessage({ id: 'general.yes' })
      : intl.formatMessage({ id: 'general.no' });
  };

  const getGeneralDataParsed = firstItem => {
    const {
      source: {
        employeeFullname = '',
        employeeIdentifier = '',
        employerName = '',
        employerIdentifier = '',
        address = {},
      } = {},
    } = firstItem || {};

    const employeeDataParsed = [
      {
        label: intl.formatMessage({ id: LABEL_REPORTS.NAME_EMPLOYEE }),
        value: employeeFullname || '',
      },
      {
        label: intl.formatMessage({ id: LABEL_REPORTS.ID_EMPLOYEE }),
        value: formatIdentifier(employeeIdentifier),
      },
    ];

    const employerDataParsed = [
      {
        label: intl.formatMessage({ id: LABEL_REPORTS.COMPANY }),
        value: employerName || '',
      },
      {
        label: intl.formatMessage({ id: LABEL_REPORTS.IDENTIFIER_EMPLOYER }),
        value: formatIdentifier(employerIdentifier),
      },
      {
        label: intl.formatMessage({ id: LABEL_REPORTS.PERIOD_CONSULTED }),
        value: queriedPeriod,
      },
      {
        label: intl.formatMessage({ id: LABEL_REPORTS.PLACE }),
        value: TimeTrackerUtils.getAddressString(address) || '',
      },
    ];

    return { employeeDataParsed, employerDataParsed };
  };

  const getRangeTime = (startDate, endDate, targetTimeZone = null) => {
    if (!startDate || !endDate) {
      return '-';
    }
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Determine timezone to use
    const tz = targetTimeZone || getTargetTimeZone(userTimeZone);

    const formatTime = (date, timezone) => {
      if (timezone) {
        // Tell moment the date is in UTC, then convert to target timezone
        // This ensures correct DST handling for the specific date
        const momentDate = moment.utc(date).tz(timezone);
        const formatted = momentDate.format('HH:mm:ss');

        return formatted;
      } else {
        // Fallback to browser timezone if no timezone specified
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();
        const formatted = `${hours.toString().padStart(2, '0')}:${minutes
          .toString()
          .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

        return formatted;
      }
    };

    const startTime = formatTime(start, tz);
    const endTime = formatTime(end, tz);
    const result = `${startTime} - ${endTime}`;

    return result;
  };

  const getRangeTimeFields = item => {
    const {
      scheduledStartAt,
      scheduledEndAt,
      scheduledBreakStartAt,
      scheduledBreakEndAt,
      actualStartAt,
      actualEndAt,
      actualBreakStartAt,
      actualBreakEndAt,
    } = item.source || {};

    const userAssisted = !!actualStartAt;

    // Journey Working Hours
    const scheduleStartAtDate = convertToDate(
      scheduledStartAt?.seconds,
      scheduledStartAt?.nanoseconds,
    );
    const scheduleEndAtDate = convertToDate(scheduledEndAt?.seconds, scheduledEndAt?.nanoseconds);

    const targetTZ = getTargetTimeZone(userTimeZone);
    const workingDay = getRangeTime(scheduleStartAtDate, scheduleEndAtDate, targetTZ);

    //Working day marking
    const actualStartAtDate = convertToDate(actualStartAt?.seconds, actualStartAt?.nanoseconds);
    const actualEndAtDate = convertToDate(actualEndAt?.seconds, actualEndAt?.nanoseconds);
    const workingDayMarking = userAssisted
      ? getRangeTime(actualStartAtDate, actualEndAtDate, targetTZ)
      : intl.formatMessage({ id: 'work.direction.table.without.marking' }) || '-';

    //Queue
    const scheduledBreakStartAtDate = convertToDate(
      scheduledBreakStartAt?.seconds,
      scheduledBreakStartAt?.nanoseconds,
    );
    const scheduledBreakEndAtDate = convertToDate(
      scheduledBreakEndAt?.seconds,
      scheduledBreakEndAt?.nanoseconds,
    );
    const queue = getRangeTime(scheduledBreakStartAtDate, scheduledBreakEndAtDate, targetTZ);

    //Marking queue
    const actualBreakStartAtDate = convertToDate(
      actualBreakStartAt?.seconds,
      actualBreakStartAt?.nanoseconds,
    );
    const actualBreakEndAtDate = convertToDate(
      actualBreakEndAt?.seconds,
      actualBreakEndAt?.nanoseconds,
    );
    const markingQueue = userAssisted
      ? getRangeTime(actualBreakStartAtDate, actualBreakEndAtDate, targetTZ)
      : intl.formatMessage({ id: 'work.direction.table.without.marking' }) || '-';

    return {
      workingDay,
      workingDayMarking,
      queue,
      markingQueue,
    };
  };

  function formatSecondsToHHMMSS(seconds) {
    const sign = seconds < 0 ? '-' : '';
    const abs = Math.abs(seconds); // get absolute
    const hrs = Math.floor(abs / 3600)
      .toString()
      .padStart(2, '0'); // get hours
    const mins = Math.floor((abs % 3600) / 60)
      .toString()
      .padStart(2, '0'); // get minutes
    const secs = (abs % 60).toString().padStart(2, '0'); // get seconds
    return `${sign}${hrs}:${mins}:${secs}`;
  }

  const setColorAccordingReport = (text, record, dataIndex) => {
    if (!record[`${dataIndex}_color`]) return <span>{text}</span>;
    return <span style={{ color: record[`${dataIndex}_color`] }}>{text}</span>;
  };

  const getAbsence = record => {
    const absence = record?.leaveType;
    const hasAttendance = record?.attendanceComplete;

    if (hasAttendance) return null;

    if (absence) {
      return GLOSSARY_ITEMS.find(item => item.code === absence)?.short;
    }

    return null;
  };

  const getObservations = record => {
    const observations = record?.observations;
    const hasAttendance = record?.attendanceComplete;

    if (hasAttendance) return null;

    return formatObservation(observations);
  };

  const actionColumn = record => {
    const disabled = isAttendanceEditInCooldown(record?.updatedAttendanceAt);
    const cooldownTooltipText = intl.formatMessage({
      id: 'work.direction.attendance.edit.cooldown.tooltip',
    });
    const editButton = (
      <Button
        className="gx-action-btn"
        shape="circle"
        disabled={disabled}
        icon={
          <span
            className={`material-icons-outlined gx-action-btn-edit ${disabled ? 'gx-text-disabled' : ''}`}
          >
            edit
          </span>
        }
        onClick={() => {
          const date = DateUtil.dtFormatToStandardFormat(record.date);
          const data = { ...record, date };
          setSelectedReportRecord(data);
          setIsDrawerOpen(true);
        }}
      />
    );

    return isFromOperonda && isAdminOrOwner ? (
      <div className="gx-w-100 gx-d-flex gx-justify-content-center">
        {disabled ? (
          <Tooltip title={cooldownTooltipText}>
            <span className="gx-d-inline-block">{editButton}</span>
          </Tooltip>
        ) : (
          editButton
        )}
      </div>
    ) : null;
  };

  const createAttendanceReport = () => {
    const title = intl.formatMessage({
      id: 'work.direction.attendance.report',
    });
    const columnsTranslated = PDF_REPORTS_COLUMNS.ATTENDANCE.map(column => ({
      ...column,
      title: column.title ? intl.formatMessage({ id: column.title }) : column.title,
      width: column.title.length === 0 ? '100px' : undefined,
      render: (val, record) =>
        column.title.length === 0
          ? actionColumn(record)
          : setColorAccordingReport(val, record, column?.dataIndex),
    })).filter(column => column.dataIndex !== 'id' || isFromOperonda); //NOTE: Remove id column if not from Operonda since just in operonda we can edit the attendance

    const dataByEmployee = camelData?.aggregations?.byEmployeeId?.buckets || [];
    let counter = camelData?.hits?.total?.value || 0;
    let output = {};

    output = dataByEmployee.reduce((acc, emp) => {
      const empKey = emp?.key;
      let allAttendanceDocs = [];

      emp?.byWeek?.buckets.forEach(week => {
        allAttendanceDocs.push(...(week?.attendanceDocs?.hits?.hits || []));
      });

      const firstItem = allAttendanceDocs[0];
      const { employeeDataParsed, employerDataParsed } = getGeneralDataParsed(firstItem);

      const source = allAttendanceDocs.map(item => {
        const data = item?.source || {};
        const actualStartAtDate = DateUtil.toDateString(data?.actualStartAt, DATE_FORMAT);
        const scheduleStartAtDate = DateUtil.toDateString(data?.scheduledStartAt, DATE_FORMAT);
        const userAssisted = !!data?.actualStartAt;
        const { workingDay, workingDayMarking, queue, markingQueue } = getRangeTimeFields(item);
        const sortDate = userAssisted
          ? data?.actualStartAt?.seconds
          : data?.scheduledStartAt?.seconds;

        return {
          id: item?._id || item.id,
          attendanceRecordId: item?._id || item.id,
          date: userAssisted ? actualStartAtDate : scheduleStartAtDate || '-',
          attendance: generalYesNo(data?.attendanceComplete),
          absence: getAbsence(data),
          observations: getObservations(data) || '-',
          updatedAttendanceAt: data?.updatedAttendanceAt,
          // COLORS RULES
          attendance_color: getColumnColor({ record: data, dataIndex: 'actualChanged' }),
          absence_color: getColumnColor({ record: data, dataIndex: 'leaveTypeChanged' }),
          observations_color: getColumnColor({ record: data, dataIndex: 'observationsChanged' }),
          workingDay: workingDay ?? '-',
          workingDayMarking: workingDayMarking ?? '-',
          queue: queue ?? '-',
          markingQueue: markingQueue ?? '-',
          employee: employeeDataParsed,
          employer: employerDataParsed,
        };
      });

      acc[empKey] = {
        title,
        id: empKey,
        columns: columnsTranslated,
        employee: employeeDataParsed,
        employer: employerDataParsed,
        source,
      };

      return acc;
    }, {});

    return { data: output, total: counter };
  };

  const createWorkingDayReport = () => {
    try {
      const title = intl.formatMessage({ id: 'work.direction.daily.report' });
      const columnsTranslated = PDF_REPORTS_COLUMNS.WORKING_DAY.map(column => ({
        ...column,
        title: column.title ? intl.formatMessage({ id: column.title }) : column.title,
        render: (val, record) =>
          column.title.length === 0
            ? actionColumn(record)
            : setColorAccordingReport(val, record, column?.dataIndex),
      })).filter(column => column.dataIndex !== 'id' || isFromOperonda); //NOTE: Remove id column if not from Operonda since just in operonda we can edit the attendance

      const dataByEmployee = camelData?.aggregations?.byEmployeeId?.buckets || [];
      let counter = camelData?.hits?.total?.value || 0;
      let output = {};

      output = dataByEmployee.reduce((acc, emp) => {
        const empKey = emp?.key;
        const outputByWeek = emp?.byWeek?.buckets.reduce((acumWeek, week) => {
          /* TABLE DATA SOURCE */
          const source = week.attendanceDocs.hits.hits.map(item => {
            const {
              scheduledStartAt,
              scheduledEndAt,
              scheduledBreakStartAt,
              scheduledBreakEndAt,
              actualStartAt,
              actualEndAt,
              actualBreakStartAt,
              actualBreakEndAt,
              otherShiftInformation = '',
              exceptionalShift = '',
            } = item.source || {};

            // User assisted - true if the user assisted the shift
            const userAssisted = !!actualStartAt;

            // Journey Working Hours
            const scheduleStartAtDate = convertToDate(
              scheduledStartAt?.seconds,
              scheduledStartAt?.nanoseconds,
            );
            const scheduleEndAtDate = convertToDate(
              scheduledEndAt?.seconds,
              scheduledEndAt?.nanoseconds,
            );

            const targetTZ = getTargetTimeZone(userTimeZone);
            const workingDay = getRangeTime(scheduleStartAtDate, scheduleEndAtDate, targetTZ);

            //Working day marking
            const actualStartAtDate = convertToDate(
              actualStartAt?.seconds,
              actualStartAt?.nanoseconds,
            );
            const actualEndAtDate = convertToDate(actualEndAt?.seconds, actualEndAt?.nanoseconds);
            const workingDayMarking = userAssisted
              ? getRangeTime(actualStartAtDate, actualEndAtDate, targetTZ)
              : intl.formatMessage({ id: 'work.direction.table.without.marking' }) || '-';

            //Queue
            const scheduledBreakStartAtDate = convertToDate(
              scheduledBreakStartAt?.seconds,
              scheduledBreakStartAt?.nanoseconds,
            );
            const scheduledBreakEndAtDate = convertToDate(
              scheduledBreakEndAt?.seconds,
              scheduledBreakEndAt?.nanoseconds,
            );
            const queue = getRangeTime(
              scheduledBreakStartAtDate,
              scheduledBreakEndAtDate,
              targetTZ,
            );

            //Marking queue
            const actualBreakStartAtDate = convertToDate(
              actualBreakStartAt?.seconds,
              actualBreakStartAt?.nanoseconds,
            );
            const actualBreakEndAtDate = convertToDate(
              actualBreakEndAt?.seconds,
              actualBreakEndAt?.nanoseconds,
            );
            const markingQueue = userAssisted
              ? getRangeTime(actualBreakStartAtDate, actualBreakEndAtDate, targetTZ)
              : intl.formatMessage({ id: 'work.direction.table.without.marking' }) || '-';

            const date = userAssisted
              ? DateUtil.toDateString(scheduledStartAt, DATE_FORMAT)
              : DateUtil.toDateString(scheduleStartAtDate, DATE_FORMAT) || '-';

            const response = {
              id: item?._id || item.id,
              attendanceRecordId: item?._id || item.id,
              date,
              workingDay: workingDay || '-',
              workingDayMarking: workingDayMarking || '-',
              queue: queue || '-',
              markingQueue: markingQueue || '-',
              missingTime: `- ${formatSecondsToHHMMSS(item?.source?.deltaMissingTime ?? 0)}` || '-',
              extraTime: `+ ${formatSecondsToHHMMSS(item?.source?.deltaExtraTime ?? 0)}` || '-',
              otherMarkings: otherShiftInformation || '-',
              observations: formatObservation(item?.source?.observations) || '-',
              dej: exceptionalShift || '-',
              updatedAttendanceAt: item?.source?.updatedAttendanceAt,

              // COLORS RULES
              workingDayMarking_color: getColumnColor({
                record: item?.source,
                dataIndex: 'actualChanged',
              }),
              markingQueue_color: getColumnColor({
                record: item?.source,
                dataIndex: 'actualBreakChanged',
              }),
              observations_color: getColumnColor({
                record: item?.source,
                dataIndex: 'observationsChanged',
              }),

              // Temporary field for sorting (will be removed)
              _sortDate: userAssisted
                ? actualStartAtDate?.toLocaleDateString('es-ES')
                : scheduleStartAtDate?.toLocaleDateString('es-ES'),
            };
            return response;
          });

          // Sort source data by date in ascending order (most recent first)
          source.sort((a, b) => {
            const dateA = a._sortDate || new Date(0);
            const dateB = b._sortDate || new Date(0);
            return dateA - dateB; // Ascending order (newest first)
          });

          // Remove temporary sorting field from all items
          source.forEach(item => {
            delete item._sortDate;
          });

          // The calculate is total workingDay - total queue
          const totalOrdinaryAgreedWorkingDay = week?.scheduledTimeSum?.value || 0;

          // The calculate is total workingDay marking - total marking queue
          const totalDayMarking = week?.actualTimeSum?.value || 0;

          // Total collation
          const collationAsSeconds = week?.scheduledBreakTimeSum?.value || 0;

          // Total marking collation
          const totalMarkingCollation = week?.actualBreakTimeSum?.value || 0;

          // Total missing time
          const totalMissingTime = week?.deltaMissingTimeSum?.value || 0;

          // Total extra time
          const extraTimetotal = week?.deltaExtraTimeSum?.value || 0;

          const finalCompensation = extraTimetotal - totalMissingTime;

          const compensationLabel = `${translateString('work.direction.compensation')} ${finalCompensation > 0 ? '+' : ''}${formatSecondsToHHMMSS(
            finalCompensation,
          )}`;

          const aggregations = [
            translateString('work.direction.weekly.totals'),
            formatSecondsToHHMMSS(totalOrdinaryAgreedWorkingDay), // Total Ordinary Agreed Working Day
            formatSecondsToHHMMSS(totalDayMarking), // Total Day Marking
            formatSecondsToHHMMSS(collationAsSeconds), // Collation
            formatSecondsToHHMMSS(totalMarkingCollation), // Marking Collation
            `- ${formatSecondsToHHMMSS(totalMissingTime)}`, // Total Missing Time
            `+ ${formatSecondsToHHMMSS(extraTimetotal)}`, // Extra Time
            '', // otherMarkings
            compensationLabel, // observations — Final Compensation
            '', // dej
          ];
          //NOTE: Add empty string from Operonda since just in operonda we can edit the attendance
          if (isFromOperonda) aggregations.push('');

          const sourceData = week.attendanceDocs.hits.hits.map(item => {
            const {
              _id: id,
              source: {
                employeeFullname = '',
                employeeIdentifier = '',
                employerName = '',
                employerIdentifier = '',
                address = {},
                workCircumstances = '',
              } = {},
            } = item;

            const employeeDataParsed = [
              {
                label: intl.formatMessage({ id: LABEL_REPORTS.NAME_EMPLOYEE }),
                value: employeeFullname || '',
              },
              {
                label: intl.formatMessage({ id: LABEL_REPORTS.ID_EMPLOYEE }),
                value: formatIdentifier(employeeIdentifier),
              },
            ];

            const employerDataParsed = [
              {
                label: intl.formatMessage({ id: LABEL_REPORTS.COMPANY }),
                value: employerName,
              },
              {
                label: intl.formatMessage({ id: LABEL_REPORTS.IDENTIFIER_EMPLOYER }),
                value: formatIdentifier(employerIdentifier),
              },
              {
                label: intl.formatMessage({ id: LABEL_REPORTS.PERIOD_CONSULTED }),
                value: queriedPeriod,
              },
              {
                label: intl.formatMessage({ id: LABEL_REPORTS.PLACE }),
                value: TimeTrackerUtils.getAddressString(address),
              },
              {
                label: intl.formatMessage({ id: LABEL_REPORTS.CIRCUNSTANCES }),
                value: workCircumstances
                  ? intl.formatMessage({ id: ATTENDANCE_WORK_CIRCUMSTANCES[workCircumstances] })
                  : '-',
              },
            ];

            return {
              title,
              id,
              employee: employeeDataParsed,
              employer: employerDataParsed,
              columns: columnsTranslated,
              source,
              summary: aggregations,
            };
          });

          acumWeek = { [week.key]: sourceData, ...acumWeek };

          return acumWeek;
        }, {});

        acc = { [empKey]: outputByWeek, ...acc };

        return acc;
      }, {});

      return {
        data: output,
        total: counter,
      };
    } catch (error) {
      return {
        data: {},
        total: 0,
        error: error.message,
      };
    }
  };

  const createHolidaysReport = () => {
    const title = intl.formatMessage({
      id: 'work.direction.holidays.report',
    });
    const columnsTranslated = PDF_REPORTS_COLUMNS.HOLIDAYS.map(column => ({
      ...column,
      title: column.title ? intl.formatMessage({ id: column.title }) : column.title,
      render: (val, record) => setColorAccordingReport(val, record, column?.dataIndex),
    }));

    // Adapt to new API structure
    const dataByEmployee =
      camelData?.result?.aggregations?.byEmployeeId?.buckets ||
      camelData?.aggregations?.byEmployeeId?.buckets ||
      [];
    let counter = camelData?.result?.hits?.total?.value || camelData?.hits?.total?.value || 0;

    let output = {};

    output = dataByEmployee.reduce((acc, emp) => {
      const empKey = emp?.key;

      // Combine all source data for this employee
      let allSourceData = [];
      let employeeDataParsed = [];
      let employerDataParsed = [];
      let id = empKey; // Use employee key as ID

      // Calculate aggregations by month for the footer using new structure
      const aggregationByMonth =
        emp?.byMonthId?.buckets?.map(ft => {
          const [monthAsNumber, year] = ft.key.split('_');
          const month = new Date(null, monthAsNumber - 1, 1).toLocaleString('es-ES', {
            month: 'long',
          });
          const monthYear = `${month?.charAt(0)?.toUpperCase() + month?.slice(1)} ${year}`;

          return {
            label: monthYear,
            value: String(ft?.workedThisMonth?.docCount ?? 0),
          };
        }) || [];

      const totalByMonth = emp?.workedHolidaysSundays?.docCount ?? 0;
      // Add total of holidays
      aggregationByMonth.push({
        label: translateString('work.direction.total.holidays'),
        value: String(totalByMonth),
      });

      // Process attendance docs directly from new structure
      const attendanceDocs = emp?.attendanceDocs?.hits?.hits || [];

      // If no direct attendance docs, fall back to old structure
      if (attendanceDocs.length === 0 && emp?.byWeek?.buckets) {
        emp.byWeek.buckets.forEach(week => {
          week.attendanceDocs.hits.hits.forEach(item => {
            attendanceDocs.push(item);
          });
        });
      }

      // Extract source data from attendance docs
      attendanceDocs.forEach(item => {
        const actualStartAt = convertToDate(
          item?.source?.actualStartAt?.seconds,
          item?.source?.actualStartAt?.nanoseconds,
        );
        const scheduledDate = convertToDate(
          item?.source?.scheduledStartAt?.seconds,
          item?.source?.scheduledStartAt?.nanoseconds,
        );

        const userAssisted = !!actualStartAt;
        const sourceData = item?._source || item?.source;
        const date = userAssisted
          ? DateUtil.toDateString(item?.source?.actualStartAt, DATE_FORMAT)
          : DateUtil.toDateString(item?.source?.scheduledStartAt, DATE_FORMAT);

        const sourceItem = {
          id: item?._id,
          TCDDA: generalYesNo(sourceData?.isCommerceAdditionalBenefit),
          date,
          absence: getAbsence(sourceData || {}) || '-',
          attendance: generalYesNo(sourceData?.attendanceComplete) || '-',
          observations: getObservations(sourceData) || '-',

          // COLORS RULES
          attendance_color: getColumnColor({
            record: sourceData,
            dataIndex: 'attendanceCompleteChanged',
          }),
          absence_color: getColumnColor({
            record: sourceData,
            dataIndex: 'leaveTypeChanged',
          }),
          observations_color: getColumnColor({
            record: sourceData,
            dataIndex: 'observationsChanged',
          }),

          // Temporary field for sorting (will be removed)
          _sortDate: userAssisted ? actualStartAt : scheduledDate,
        };

        allSourceData.push(sourceItem);

        // Get employee and employer data from the first item only
        if (employeeDataParsed.length === 0) {
          employeeDataParsed = [
            {
              label: intl.formatMessage({
                id: LABEL_REPORTS.NAME_EMPLOYEE,
              }),
              value: sourceData?.employeeFullname || '',
            },
            {
              label: intl.formatMessage({
                id: LABEL_REPORTS.ID_EMPLOYEE,
              }),
              value: formatIdentifier(sourceData?.employeeIdentifier),
            },
          ];

          employerDataParsed = [
            {
              label: intl.formatMessage({
                id: LABEL_REPORTS.COMPANY,
              }),
              value: sourceData?.employerName,
            },
            {
              label: intl.formatMessage({
                id: LABEL_REPORTS.IDENTIFIER_EMPLOYER,
              }),
              value: formatIdentifier(sourceData?.employerIdentifier),
            },
            {
              label: intl.formatMessage({
                id: LABEL_REPORTS.PERIOD_CONSULTED,
              }),
              value: queriedPeriod,
            },
            {
              label: intl.formatMessage({
                id: LABEL_REPORTS.PLACE,
              }),
              value: TimeTrackerUtils.getAddressString(sourceData?.address),
            },
            {
              label: intl.formatMessage({
                id: LABEL_REPORTS.JOB,
              }),
              value: sourceData?.employeeJobName,
            },
          ];
        }
      });

      // Sort source data by date in ascending order (newest first)
      allSourceData.sort((a, b) => {
        // Handle cases where _sortDate might be null or undefined
        const dateA = a._sortDate || new Date(0);
        const dateB = b._sortDate || new Date(0);
        return dateA - dateB; // Ascending order (newest first)
      });

      // Remove temporary sorting field from all items
      allSourceData.forEach(item => {
        delete item._sortDate;
      });

      // Create flat structure for this employee (only one object per employee)
      acc[empKey] = {
        title,
        id,
        employee: employeeDataParsed,
        employer: employerDataParsed,
        columns: columnsTranslated,
        source: allSourceData,
        footer: aggregationByMonth,
      };

      return acc;
    }, {});

    return {
      data: output,
      total: counter,
    };
  };

  const createModificationsReport = () => {
    const title = intl.formatMessage({
      id: 'work.direction.modifications.report',
    });

    const columnsTranslated = PDF_REPORTS_COLUMNS.MODIFICATIONS.map(column => ({
      ...column,
      title: column.title ? intl.formatMessage({ id: column.title }) : column.title,
    }));
    const dataByEmployee = camelData?.aggregations?.byEmployeeId?.buckets || [];
    let counter = camelData?.hits?.total?.value || 0;
    let output = {};

    output = dataByEmployee.reduce((acc, emp) => {
      const empKey = emp?.key;

      //NOTE: Exclude if the employee is not a shift work, this is a workaround to avoid showing the empty state when the user is not a shift work
      const isShiftWork = isAllUsersSelected || usersSelectedShiftWorkIds.includes(empKey);
      if (!isShiftWork) {
        return acc;
      }

      const outputByWeek = emp?.byWeek?.buckets.reduce((acumWeek, week) => {
        /* TABLE DATA SOURCE */
        const source = week.shiftLogsDocs.hits.hits.map(item => {
          const {
            assignDate,
            shiftDetail = '',
            newShiftDetail = '',
            shiftExtension,
            newAssignDate,
            shiftStart,
            extension,
            application,
          } = item.source || {};

          const assignDateObject = convertToDate(assignDate?.seconds, assignDate?.nanoseconds);

          return {
            id: item?._id,
            assignDate: DateUtil.toDateString(assignDate, DATE_FORMAT) || '-',
            detailShift: shiftDetail || '-',
            shiftExtension: translateValue(shiftExtension) || '-',
            shiftAssignedDate: DateUtil.toDateString(newAssignDate, DATE_FORMAT) || '-',
            startShift: DateUtil.toDateString(shiftStart, DATE_FORMAT) || '-',
            newSchedule: newShiftDetail || '-',
            extension: translateValue(extension) || '-', // TODO: Translate value like key value
            request: translateValue(application) || '-', // TODO: Translate value like key value
            observations: getObservations(item?.source) || '-',
            // Temporary field for sorting (will be removed)
            _sortDate: assignDateObject,
          };
        });

        // Sort source data by date in ascending order (most recent first)
        source.sort((a, b) => {
          const dateA = a._sortDate || new Date(0);
          const dateB = b._sortDate || new Date(0);
          return dateA - dateB; // Ascending order (newest first)
        });

        // Remove temporary sorting field from all items
        source.forEach(item => {
          delete item._sortDate;
        });

        const sourceData = week.shiftLogsDocs.hits.hits.map(item => {
          const {
            _id: id,
            source: {
              employeeFullname = '',
              employeeFullName = '',
              employeeIdentifier = '',
              employerName = '',
              employerIdentifier = '',
              address = {},
            } = {},
          } = item;

          const employeeDataParsed = [
            {
              label: intl.formatMessage({
                id: LABEL_REPORTS.NAME_EMPLOYEE,
              }),
              value: employeeFullname || employeeFullName || '',
            },
            {
              label: intl.formatMessage({
                id: LABEL_REPORTS.ID_EMPLOYEE,
              }),
              value: formatIdentifier(employeeIdentifier),
            },
          ];

          const employerDataParsed = [
            {
              label: intl.formatMessage({
                id: LABEL_REPORTS.COMPANY,
              }),
              value: employerName,
            },
            {
              label: intl.formatMessage({
                id: LABEL_REPORTS.IDENTIFIER_EMPLOYER,
              }),
              value: formatIdentifier(employerIdentifier),
            },
            {
              label: intl.formatMessage({
                id: LABEL_REPORTS.PERIOD_CONSULTED,
              }),
              value: queriedPeriod,
            },
            {
              label: intl.formatMessage({
                id: LABEL_REPORTS.PLACE,
              }),
              value: TimeTrackerUtils.getAddressString(address),
            },
          ];

          return {
            title,
            id,
            employee: employeeDataParsed,
            employer: employerDataParsed,
            columns: columnsTranslated,
            source,
          };
        });

        acumWeek = {
          [week.key]: sourceData,
          ...acumWeek,
        };

        return acumWeek;
      }, {});

      acc = {
        [empKey]: outputByWeek,
        ...acc,
      };

      return acc;
    }, {});

    return {
      data: output,
      total: counter,
    };
  };

  const createIssuesReport = () => {
    const title = intl.formatMessage({
      id: 'work.direction.issues.report',
    });
    const columnsTranslated = PDF_REPORTS_COLUMNS.ISSUES.map(column => ({
      ...column,
      title: column.title ? intl.formatMessage({ id: column.title }) : column.title,
    }));

    const issuesData = camelData?.hits?.hits || [];
    let counter = camelData?.hits?.total?.value || 0;
    let output = {};

    // Process each issue and group by employerId
    issuesData.forEach((item, index) => {
      const { id, source: itemSource } = item;
      const {
        startAt,
        endAt,
        description,
        employerName = '',
        employerIdentifier = '',
        employerId = '',
      } = itemSource || {};

      // Use employerId as key, fallback to employerIdentifier if not available
      const employerKey = employerId || employerIdentifier || `unknown_${index}`;

      // Handle both formats: _seconds/_nanoseconds and seconds/nanoseconds
      const startSeconds = startAt?._seconds || startAt?.seconds || 0;
      const startNanoseconds = startAt?._nanoseconds || startAt?.nanoseconds || 0;
      const endSeconds = endAt?._seconds || endAt?.seconds || 0;
      const endNanoseconds = endAt?._nanoseconds || endAt?.nanoseconds || 0;
      const startAsDate = convertToDate(startSeconds, startNanoseconds);
      const endAsDate = convertToDate(endSeconds, endNanoseconds);
      const startString = getDateTimeString(startAsDate);
      const endString = getDateTimeString(endAsDate);

      const sourceItem = {
        id,
        start: startString ?? '',
        end: endString ?? '',
        description,
        // Temporary field for sorting (will be removed)
        _sortDate: startAsDate,
      };

      // Initialize employer group if it doesn't exist
      if (!output[employerKey]) {
        const employerDataParsed = [
          {
            label: intl.formatMessage({
              id: LABEL_REPORTS.COMPANY,
            }),
            value: employerName,
          },
          {
            label: intl.formatMessage({
              id: LABEL_REPORTS.IDENTIFIER_EMPLOYER,
            }),
            value: formatIdentifier(employerIdentifier),
          },
          {
            label: intl.formatMessage({
              id: LABEL_REPORTS.PERIOD_CONSULTED,
            }),
            value: queriedPeriod,
          },
        ];

        output[employerKey] = {
          title,
          id: employerKey,
          employee: [],
          employer: employerDataParsed,
          columns: columnsTranslated,
          source: [],
        };
      }

      // Add issue to employer's source array
      output[employerKey].source.push(sourceItem);
    });

    // Sort each employer's issues by date
    Object.values(output).forEach(employerData => {
      employerData.source.sort((a, b) => {
        const dateA = a._sortDate || new Date(0);
        const dateB = b._sortDate || new Date(0);
        return dateA - dateB; // Ascending order (oldest first)
      });

      // Remove temporary sorting field
      employerData.source.forEach(item => {
        delete item._sortDate;
      });
    });

    return {
      data: output,
      total: counter,
    };
  };

  const createReport = useCallback(() => {
    switch (reportType) {
      case KIND_OF_REPORTS.ATTENDANCE:
        return createAttendanceReport();
      case KIND_OF_REPORTS.DAILY:
        return createWorkingDayReport();
      case KIND_OF_REPORTS.HOLIDAYS:
        return createHolidaysReport();
      case KIND_OF_REPORTS.MODIFICATIONS:
        return createModificationsReport();
      case KIND_OF_REPORTS.ISSUES:
        return createIssuesReport();
      default:
        return null;
    }
  }, [reportType, camelData, queriedPeriod]);

  useEffect(() => {
    if (reset) {
      setReportData({});
      setReportDataTimeTracker({});
      return;
    }
    if (camelData) {
      const res = createReport();
      const dataMerged = mergeReportData(reportData.data, res.data, reportType);
      let dataCount = camelData?.aggregations?.byEmployeeId?.buckets.reduce(
        (acum, emp) => acum + emp?.doc_count || 0,
        0,
      );

      const hasMoreData = dataCount < res.total;

      setReportData({
        total: res.total,
        data: dataMerged,
        hasMore: hasMoreData,
      });
      setReportDataTimeTracker({
        total: res.total,
        data: dataMerged,
        hasMore: hasMoreData,
      });
      if (res.error) {
        setError(res.error);
      }
    }
  }, [camelData]);

  return {
    data: reportData,
    error,
  };
};

export default useTimeTrackerReportFactory;
